import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, Box, Grid, InputAdornment, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../formik/FormikYesNoAutocomplete";
import { FormikAutocomplete } from "../../../../../../../../formik/formik-autocomplete/FormikAutocomplete";
import { FormikDatePicker } from "../../../../../../../../formik/formik-date-picker/FormikDatePicker";
import { FormikZipCodeField } from "../../../../../../../../formik/formik-zipcode-field/FormikZipCodeField";
import { useDropdownOptions } from "../../../../../../../../hooks/useDropdownOptions";
import { ProceedingParticipantSearchResult, ProceedingParticipantType } from "../../../../../../../api/ClientFileApi";
import { FieldSpacer } from "../../../../../../../asset-inventory/components/field-spacer/FieldSpacer";
import { ParticipantSearchSelect } from "../../../../../../../asset-inventory/components/participant-search-select/ParticipantSearchSelect";
import { AssetsMaxLength } from "../../../../../../../asset-inventory/views/assets/AssetsView.types";
import { ClaimCollateralHoldingFormValues, ClaimFormValues } from "../../../../ClaimFormFields.types";
import { HOLDING_COLLATERAL_TYPES, HoldingCollateralType } from "../../Collaterals.utils";

const FULL = 12;
const HALF = { xs: 12, md: 6 };
const WIDE = { xs: 12, md: 8 };
const NARROW = { xs: 12, md: 4 };
const KEY = "claims.collaterals.newHolding";

interface Props {
  formik: FormikProps<ClaimFormValues>;
  index: number;
  type: HoldingCollateralType;
  collateralKey: string;
}

/**
 * The asset a security creates in the client's asset schedule. Every field is optional: the asset can be completed
 * in the asset schedule later, which is also the only place it is edited once saved.
 */
export function NewHoldingFields({ formik, index, type, collateralKey }: Props) {
  const { t } = useTranslation();
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const { countries } = useDropdownOptions();

  const holding = formik.values.collaterals[index]?.newHolding;
  if (!holding) {
    return null;
  }

  const config = HOLDING_COLLATERAL_TYPES[type];
  const isInsurance = config.participantType === ProceedingParticipantType.Insurance;
  const path = (field: keyof ClaimCollateralHoldingFormValues) => `collaterals.${index}.newHolding.${field}` as const;
  const fieldId = (field: keyof ClaimCollateralHoldingFormValues) => `claim-collateral-${collateralKey}-${field}`;
  const titleId = `claim-collateral-${collateralKey}-new-holding-title`;

  const fillFromParticipant = (participant: ProceedingParticipantSearchResult | null) => {
    setSelectedParticipantId(participant?.proceedingParticipantId ?? null);
    if (!participant) {
      return;
    }
    void formik.setFieldValue(path("institutionName"), participant.name);
    void formik.setFieldValue(path("street"), participant.street ?? null);
    void formik.setFieldValue(path("houseNumber"), participant.houseNumber ?? null);
    void formik.setFieldValue(path("zipCode"), participant.zipCode ?? null);
    void formik.setFieldValue(path("city"), participant.city ?? null);
    void formik.setFieldValue(path("country"), participant.country ?? null);
  };

  const text = (field: keyof ClaimCollateralHoldingFormValues, label: string, maxLength: number) => (
    <FormikTextField
      id={fieldId(field)}
      formik={formik}
      path={path(field)}
      label={label}
      slotProps={{ input: { inputProps: { maxLength } } }}
    />
  );

  return (
    <Box component="section" aria-labelledby={titleId} sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2 }}>
      <Typography
        id={titleId}
        variant="overline"
        component="h4"
        sx={{ display: "block", mb: 2, fontWeight: 600, color: "text.secondary" }}
      >
        {t(`${KEY}.title`)}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={FULL}>
          <ParticipantSearchSelect
            id={`${fieldId("institutionName")}-selection`}
            label={t(isInsurance ? `${KEY}.selectionInsurance` : `${KEY}.selectionBank`)}
            type={config.participantType}
            selectedId={selectedParticipantId}
            onChange={fillFromParticipant}
          />
        </Grid>
        <Grid size={HALF}>
          {text(
            "institutionName",
            t(isInsurance ? `${KEY}.institutionInsurance` : `${KEY}.institutionBank`),
            AssetsMaxLength.institutionName
          )}
        </Grid>
        <Grid size={HALF}>
          {text("accountNumber", t(`${KEY}.${config.numberLabel}`), AssetsMaxLength.accountNumber)}
        </Grid>
        <Grid size={WIDE}>{text("street", t(`${KEY}.street`), AssetsMaxLength.street)}</Grid>
        <Grid size={NARROW}>{text("houseNumber", t(`${KEY}.houseNumber`), AssetsMaxLength.houseNumber)}</Grid>
        <Grid size={NARROW}>
          <FormikZipCodeField
            id={fieldId("zipCode")}
            formik={formik}
            path={path("zipCode")}
            label={t(`${KEY}.zipCode`)}
            slotProps={{ input: { inputProps: { maxLength: AssetsMaxLength.zipCode } } }}
          />
        </Grid>
        <Grid size={NARROW}>{text("city", t(`${KEY}.city`), AssetsMaxLength.city)}</Grid>
        <Grid size={NARROW}>
          <FormikAutocomplete
            id={fieldId("country")}
            formik={formik}
            path={path("country")}
            label={t(`${KEY}.country`)}
            options={countries}
            freeSolo
          />
        </Grid>
        <Grid size={HALF}>
          <FormikYesNoAutocomplete
            id={fieldId("isValueKnown")}
            formik={formik}
            path={path("isValueKnown")}
            label={t(`${KEY}.isValueKnown`)}
          />
        </Grid>
        <Grid size={HALF} />
        {holding.isValueKnown === false && (
          <Grid size={FULL}>
            <Alert severity="info">{t(`${KEY}.valueUnknownHint`)}</Alert>
            <FieldSpacer />
          </Grid>
        )}
        {holding.isValueKnown === true && (
          <>
            <Grid size={HALF}>
              <FormikDecimalTextField
                id={fieldId("value")}
                formik={formik}
                path={path("value")}
                label={t(`${KEY}.value`)}
                decimalScale={2}
                minValue={0}
                slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
              />
            </Grid>
            <Grid size={HALF}>
              <FormikDatePicker formik={formik} path={path("valueDate")} label={t(`${KEY}.valueDate`)} />
            </Grid>
          </>
        )}
        <Grid size={FULL}>
          <FormikTextField
            id={fieldId("remarks")}
            formik={formik}
            path={path("remarks")}
            label={t(`${KEY}.remarks`)}
            multiline
            minRows={2}
            slotProps={{ input: { inputProps: { maxLength: AssetsMaxLength.remarks } } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
